import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { EfficiencyFlagProps } from '@packages/shared';
import { Card, CardContent, Divider, EfficiencyFlag, Stack } from '@packages/shared';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTracking } from '@packages/tracking';
import { Download } from '../Download';

const EfficiencyFlagListFragment = graphql(/* GraphQL */ `
  fragment EfficiencyFlagListFragment on Product {
    tireEfficiency {
      link
      flags {
        level
        type
      }
    }
    powerEfficiencyFlags {
      color
      level
      link
      text
      hasOldLabel
    }
    downloads {
      type
      ...DownloadFragment
    }
  }
`);

export type EfficiencyFlagListProps = {
  /**
   * Boolean to decide if layout should be rendered as row or column.
   * @default false
   * */
  horizontal?: boolean;
  /** List with efficiencyFlag properties to simply render component. */
  efficiencyFlagsData: FragmentType<typeof EfficiencyFlagListFragment>;
  /**
   * Size of the EfficiencyFlags.
   * @default "large"
   */
  variant?: EfficiencyFlagProps['variant'];
};

/**
 * This maps the type from our product data to the correct format for the component `EfficiencyFlag`
 */
const tireEfficiencyTypeMap: { [key: string]: EfficiencyFlagProps['type'] } = {
  WETGRIP: 'wetGrip',
  FUELEFFICIENCY: 'fuelEfficiency',
  EXTERNALROLLINGNOISE: 'externalRollingNoise',
};

const messages = defineMessages({
  WETGRIP: {
    defaultMessage: 'Nasshaftung',
    id: 'efficiencyflag.dialog.title.wetgrip',
  },
  FUELEFFICIENCY: {
    defaultMessage: 'Kraftstoffeffizienz',
    id: 'efficiencyflag.dialog.title.fuelEfficiency',
  },
  EXTERNALROLLINGNOISE: {
    defaultMessage: 'Rollgeräusch',
    id: 'efficiencyflag.dialog.title.externalRollingNoise',
  },
});

/**
 * EfficiencyFlagList is a list of the `EfficiencyFlag` [component](https://empiriecom.gitlab.io/core/frontend/turborepo/?path=/docs/components-efficiencyflag--tire-efficiency).
 * It has a special layout variant used in the detail view.
 * */
export const EfficiencyFlagList: FC<EfficiencyFlagListProps> = ({
  efficiencyFlagsData,
  horizontal = false,
  variant = 'large',
}) => {
  const { powerEfficiencyFlags, tireEfficiency, downloads } = getFragmentData(
    EfficiencyFlagListFragment,
    efficiencyFlagsData,
  );

  // Frontend logic - this should be removed once the gql backend supports query batching. -> https://empiriecom.atlassian.net/browse/INFORM-2588
  const productDataSheetsDownloads = downloads.filter(
    (download) => download.type === 'PRODUCT_DATASHEET',
  );

  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();

  const trackClick = (eventType: string) => () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'noteLayer',
      eventLabel: 'detailview',
      eventValue: `EEK Label(${eventType})`,
    });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1} direction={horizontal ? 'row' : 'column'}>
          {powerEfficiencyFlags &&
            powerEfficiencyFlags.map((flag, idx) => (
              <EfficiencyFlag
                text={flag.level}
                type={flag.hasOldLabel ? 'energyEfficiencyOld' : 'energyEfficiency'}
                labelColor={flag.color as EfficiencyFlagProps['labelColor']}
                // label next to the flag should only be shown if multiple flags are shown
                label={powerEfficiencyFlags.length > 1 ? flag.text : undefined}
                variant={variant}
                overlay={{ title: flag.text, imgUrl: flag.link }}
                onClickHandler={trackClick('powerEfficiencyFlag')}
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
              />
            ))}
          {tireEfficiency &&
            tireEfficiency.flags &&
            tireEfficiency.flags.map((flag) => (
              <EfficiencyFlag
                text={flag.level}
                type={tireEfficiencyTypeMap[flag.type]}
                variant={variant}
                overlay={{
                  title: formatMessage(messages[flag.type]),
                  imgUrl: tireEfficiency.link,
                }}
                onClickHandler={trackClick('tireEfficiencyFlag')}
                key={flag.level}
              />
            ))}
        </Stack>
      </CardContent>
      {productDataSheetsDownloads.length > 0 && (
        <>
          <Divider />
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              '&:last-child': { pb: 1 },
            }}
          >
            <Stack direction="column" spacing={1} alignItems="center">
              {productDataSheetsDownloads.map((download, idx) => (
                <Download
                  renderIcon={false}
                  downloadData={download}
                  // index should only be shown, if there is more then one download
                  idx={productDataSheetsDownloads.length > 1 ? idx + 1 : undefined}
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                />
              ))}
            </Stack>
          </CardContent>
        </>
      )}
    </Card>
  );
};
